// src/shared/pages/LoginPage.tsx
// Login — nova identidade Projectlys (Tailwind + UI kit)

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalAuth } from '../contexts/GlobalAuthContext';
import { BRAND } from '../config/brand';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ThemeToggle } from '../components/ui/ThemeToggle';

const LogoMark: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 40 40" fill="none" className={className} aria-hidden>
    <rect x="4" y="16" width="6" height="20" rx="2" fill="currentColor" opacity="0.4" />
    <rect x="13" y="10" width="6" height="26" rx="2" fill="currentColor" opacity="0.6" />
    <rect x="22" y="6" width="6" height="30" rx="2" fill="currentColor" opacity="0.8" />
    <rect x="31" y="4" width="6" height="32" rx="2" fill="currentColor" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const quickUsers = [
  { role: 'Admin', user: 'admin', pass: 'admin123' },
  { role: 'Manager', user: 'sarah.manager', pass: 'sarah123' },
  { role: 'Employee', user: 'john.doe', pass: 'john123' },
];

// Atalhos de credencial do ambiente de teste. Para desligar quando o sistema
// entrar em uso real, basta definir REACT_APP_HIDE_QUICK_LOGIN=true no build.
const showQuickAccess = process.env.REACT_APP_HIDE_QUICK_LOGIN !== 'true';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading, error } = useGlobalAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(username, password);
      navigate('/quality-management');
    } catch {
      // erro tratado pelo contexto
    }
  };

  return (
    <div className="relative flex min-h-screen bg-background">
      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      {/* Branding (lado esquerdo) */}
      <div className="relative hidden w-1/2 overflow-hidden bg-brand lg:flex">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(110% 110% at 0% 0%, rgb(91 130 255) 0%, rgb(43 92 255) 45%, rgb(22 46 140) 100%)',
          }}
        />
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }} />
        <div className="relative z-10 flex w-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <LogoMark className="h-9 w-9 text-white" />
            <span className="font-display text-xl font-bold tracking-tight">{BRAND.name}</span>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-3xl font-bold leading-tight">
              Planeje, acompanhe e entregue cada projeto.
            </h1>
            <p className="mt-4 text-md text-white/80">{BRAND.tagline}</p>
          </div>
          <p className="text-sm text-white/60">© {BRAND.year} {BRAND.copyrightOwner}</p>
        </div>
      </div>

      {/* Formulário (lado direito) */}
      <div className="flex w-full items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <LogoMark className="h-9 w-9 text-brand" />
            <span className="font-display text-xl font-bold text-foreground">{BRAND.name}</span>
          </div>

          <div className="mb-8">
            <h2 className="font-display text-2xl font-bold text-foreground">Entrar</h2>
            <p className="mt-1 text-base text-foreground-muted">Acesse com suas credenciais</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {error && (
              <div
                data-testid="login-error"
                className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <Input
              id="username"
              name="username"
              label="Usuário"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Digite seu usuário"
              required
              disabled={loading}
              autoComplete="username"
              leftIcon={<UserIcon />}
              data-testid="login-username"
            />

            <div className="relative">
              <Input
                id="password"
                name="password"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                required
                disabled={loading}
                autoComplete="current-password"
                leftIcon={<LockIcon />}
                data-testid="login-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                tabIndex={-1}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                className="absolute right-3 top-[34px] text-foreground-subtle hover:text-foreground"
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>

            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={loading}
              disabled={loading || !username || !password}
              data-testid="login-submit"
            >
              Entrar
            </Button>
          </form>

          {/* Atalhos de credencial do ambiente de teste */}
          {showQuickAccess && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">Acesso rápido</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="grid grid-cols-3 gap-2.5">
                {quickUsers.map((q) => (
                  <button
                    key={q.user}
                    type="button"
                    onClick={() => {
                      setUsername(q.user);
                      setPassword(q.pass);
                    }}
                    className="flex flex-col items-start gap-0.5 rounded-lg border border-border bg-surface-1 px-3 py-2.5 text-left transition-colors hover:border-border-strong hover:bg-surface-2"
                  >
                    <span className="text-sm font-semibold text-foreground">{q.role}</span>
                    <span className="truncate text-xs text-foreground-muted">{q.user}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
